<template>
  <div class="offers-container">
    <h2 class="title">{{ t("offers.offers") }}</h2>
    <div v-if="offers.length === 0" class="no-offers">
      <p data-cy="no-offers-text">{{ t("offers.no-pending") }}</p>
    </div>
    <div v-for="offer in offers" :key="offer.rowKey" class="offer-item">
      <div class="offer-info">
        <div class="offer-title">{{ offer.title }}</div>
        <div class="offer-discount">
          {{ offer.discountLabel }}
        </div>
      </div>
      <SInputGroup class="offer-price">
        <SInputGroupAddon align="inline-start">
          {{ bookingCurrency }}
        </SInputGroupAddon>
        <SInputGroupInput
          :model-value="offer.amount"
          @update:model-value="(v) => (offer.amount = String(v))"
          disabled
        />
      </SInputGroup>
      <SButton
        type="button"
        variant="outline"
        size="icon-sm"
        class="remove-btn rounded-full"
        :disabled="!offer.stillPending"
        aria-label="remove offer"
        data-cy="offer-remove-btn"
        @click="confirmRemovingOffer(offer)"
      >
        <XIcon style="font-size: 16px; color: red" />
      </SButton>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, h } from "vue";
import type { Component } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import {
  Currency,
  Discount,
  DiscountType,
  OrderDiscount,
  OrderDiscountStatus,
} from "@smeetz/order-common";
import { IOfferV1, OfferDiscountType } from "@smeetz/offers-common";
import { useGroupOffersStore } from "@/stores/group-offers.store";
import { removeDiscount } from "@/utils/vue-helpers/orders.helper";
import { LogService } from "@/services/logging";
import {
  SButton,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
} from "@smeetz/ui-core";
import { XIcon } from "@smeetz/ui-icons";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";

interface IAppliedOffer {
  rowKey: string;
  orderDiscountId: string;
  offerId: number;
  /** Ticket name (priceName) — main label */
  title: string;
  /** Offer name + percentage/amount — secondary label */
  discountLabel: string;
  amount: string;
  stillPending: boolean;
}

export default defineComponent({
  name: "offers",
  components: {
    SButton,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    XIcon,
  },
  setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const groupOffersStore = useGroupOffersStore();
    const { offers: groupOffers } = storeToRefs(groupOffersStore);

    const bookingCurrency = computed(() => {
      return orderStore.selectedOrder?.orderSession?.currency || Currency.CHF;
    });

    const offers = computed<IAppliedOffer[]>(() => {
      const orderDiscounts: OrderDiscount[] =
        orderStore.selectedOrder?.orderSession?.orderDiscounts ?? [];
      const discounts: Discount[] =
        orderStore.selectedOrder?.orderSession?.discounts ?? [];
      const orderLines =
        orderStore.selectedOrder?.orderSession?.orderLines ?? [];

      const rows: IAppliedOffer[] = [];

      const offerOrderDiscounts = orderDiscounts.filter(
        (od) =>
          od.type === DiscountType.OFFER &&
          od.status === OrderDiscountStatus.Pending
      );

      for (const od of offerOrderDiscounts) {
        const offerDetails = groupOffers.value.find((o) => o.id === od.offerId);
        const offerName = od.code
          ? String(od.code)
          : offerDetails?.name || t("label.offer-discount");

        // Group line-level discounts by priceId so each ticket type gets a row
        const byPriceId = new Map<
          string,
          { priceName: string; totalAmount: number }
        >();

        const lineDiscounts = discounts.filter(
          (d) => d.orderDiscountId === od.id
        );
        for (const d of lineDiscounts) {
          const line = orderLines.find((l) => l.id === d.lineId);
          if (!line || !line.priceId) continue;

          const existing = byPriceId.get(line.priceId) ?? {
            priceName: line.priceName ?? "",
            totalAmount: 0,
          };
          existing.totalAmount += Number(d.value || 0);
          byPriceId.set(line.priceId, existing);
        }

        for (const [priceId, info] of byPriceId) {
          rows.push({
            rowKey: `${od.id}-${priceId}`,
            orderDiscountId: od.id,
            offerId: od.offerId ?? 0,
            title: info.priceName,
            discountLabel: buildDiscountLabel(offerDetails, od, offerName),
            amount: info.totalAmount.toFixed(2),
            stillPending: od.status === OrderDiscountStatus.Pending,
          });
        }
      }

      return rows;
    });

    function buildDiscountLabel(
      offer: IOfferV1 | undefined,
      od: OrderDiscount,
      offerName: string
    ): string {
      if (od.percentDiscount) {
        return `${offerName} ${Math.round(od.percentDiscount * 100)}%`;
      }
      if (od.flatDiscount) {
        return `${offerName} ${od.flatDiscount.toFixed(2)} ${
          bookingCurrency.value
        }`;
      }
      if (offer?.discountType === OfferDiscountType.TYPE_BUY_X_GET_Y_FREE) {
        return offerName;
      }
      return offerName;
    }

    /**
     * Builds the rich body shown inside the remove-offer confirmation.
     * Returns a one-shot Component bound to the offer being removed.
     *
     * @param {IAppliedOffer} offer - The offer the user is about to remove.
     * @returns {Component} Vue component to pass as `showConfirm({ body })`.
     */
    function buildRemoveOfferBody(offer: IAppliedOffer): Component {
      const currency = bookingCurrency.value;
      return {
        render: () =>
          h("div", { class: "flex flex-col gap-4" }, [
            h(
              "p",
              { class: "text-sm text-muted-foreground" },
              t("offers.confirm-remove-msg")
            ),
            h(
              "div",
              {
                class:
                  "flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4",
              },
              [
                h("div", { class: "min-w-0 flex-1" }, [
                  h(
                    "div",
                    {
                      class: "truncate text-base font-semibold text-foreground",
                    },
                    offer.title
                  ),
                  h(
                    "div",
                    { class: "text-xs text-muted-foreground" },
                    offer.discountLabel
                  ),
                ]),
                h(
                  "div",
                  { class: "text-sm font-medium tabular-nums" },
                  `${currency} ${offer.amount}`
                ),
              ]
            ),
          ]),
      };
    }

    async function confirmRemovingOffer(offer: IAppliedOffer) {
      const confirmed = await showConfirm({
        title: t("offers.remove-offer"),
        body: buildRemoveOfferBody(offer),
        variant: "destructive",
        confirmText: t("offers.yes-remove"),
      });
      if (confirmed) {
        LogService.info(`User confirmed pending offer removal`, {
          params: offer,
        });
        try {
          await removeDiscount(offer.orderDiscountId);
        } catch (error) {
          LogService.info("Offer removal failed", { error });
        }
      } else {
        LogService.info(`User did not confirm offer cancellation`, {
          params: offer,
        });
      }
    }

    return {
      t,
      offers,
      confirmRemovingOffer,
      bookingCurrency,
    };
  },
});
</script>

<style lang="scss" scoped>
.offers-container {
  height: 90%;
  min-width: 600px;
  padding: 1em 0;
  font-size: 16px;
}

.offer-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.offer-info {
  flex: 1;
}

.offer-title {
  font-weight: medium;
  font-size: 16px;
}

.offer-discount {
  color: #888;
  font-size: 12px;
}

.offer-price {
  width: 300px;
  margin-right: 10px;
}

.title {
  margin-bottom: 30px;
  font-size: 24px;
  font-weight: 500;
}

.remove-btn {
  border: 2px solid red;
  background-color: white;
  color: red;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.remove-btn:hover {
  background-color: #ffe5e5;
}
.no-offers {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  font-size: 18px;
  color: #888;
}
::v-deep .el-input-group__prepend {
  color: black;
}
::v-deep .el-input__inner {
  color: black;
  -webkit-text-fill-color: black !important;
}
</style>
